import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { Page, PageErrorState, Typography, useOverlayContext, FunctionalLoader } from 'seagull';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useEmailAuthCompletion } from '~/hooks/useEmailAuthCompletion';
import { useIntegration } from '~/hooks/useIntegration';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { integrationModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import { AccountingSetupPage } from '../Accounting/AccountingSetupPage/AccountingSetupPage';
import { ClientHubSetupPage } from '../PracticeManagement/ClientHubSetupPage';
import { FinancialCentsSetupPage } from '../PracticeManagement/FinancialCentsSetupPage';
import { KarbonSetupPage } from '../PracticeManagement/KarbonSetupPage';
import { KeeperSetupPage } from '../PracticeManagement/KeeperSetupPage';
import styles from './AvailableAppsPage.module.scss';
import ClientHubCard from './ClientHubCard/ClientHubCard';
import CountCard from './CountCard/CountCard';
import FinancialCentsCard from './FinancialCentsCard/FinancialCentsCard';
import GmailCard from './GmailCard/GmailCard';
import HubspotViaZapierCard from './HubspotViaZapierCard/HubspotViaZapierCard';
import HubspotViaZapierPage from './HubspotViaZapierPage/HubspotViaZapierPage';
import KarbonCard from './KarbonCard/KarbonCard';
import KeeperCard from './Keeper/KeeperCard/KeeperCard';
import LevvyCard from './LevvyCard/LevvyCard';
import MakeCard from './MakeCard/MakeCard';
import MondayCard from './MondayCard/MondayCard';
import OutlookCard from './OutlookCard/OutlookCard';
import QboCard from './QboCard/QboCard';
import SlackViaZapierCard from './SlackViaZapierCard/SlackViaZapierCard';
import SlackViaZapierPage from './SlackViaZapierPage/SlackViaZapierPage';
import XeroCard from './XeroCard/XeroCard';
import ZapierCard from './ZapierCard/ZapierCard';
const title = 'Integrations';
const subheader = 'Manage your integrations';
const IntegrationSection = (param)=>{
    let { title, children } = param;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                weight: "normal",
                className: styles.title,
                children: title
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.cards,
                children: children
            })
        ]
    });
};
export function AvailableAppsPage() {
    const { fetchIntegrations } = useStoreActions();
    const { data: integrations, error } = useStore((state)=>state.integrations);
    const businessId = useStore(selectors.user.businessId);
    const [isLoading, setIsLoading] = useState(true);
    const { connect: connectToQbo } = useIntegration();
    const { changeLocation } = useIoContext();
    const karbonIntegration = integrationModel.karbonIntegration(integrations);
    const clientHubIntegration = integrationModel.clientHubIntegration(integrations);
    const financialCentsIntegration = integrationModel.financialCentsIntegration(integrations);
    const keeperIntegration = integrationModel.keeperIntegration(integrations);
    const xeroIntegration = integrationModel.xeroIntegration(integrations);
    const gmailIntegration = integrationModel.gmailIntegration(integrations);
    const outlookIntegration = integrationModel.outlookIntegration(integrations);
    const { showErrorToast } = useOverlayContext();
    useSetDocumentTitle(title);
    useOnMount(()=>{
        if (integrations) {
            setIsLoading(false);
        } else {
            fetchIntegrations().then(()=>setIsLoading(false));
        }
    });
    useEmailAuthCompletion({
        businessId,
        onSuccess: async ()=>{
            await fetchIntegrations();
            setIsLoading(false);
        },
        onError: (errorMessage, toastId)=>{
            showErrorToast(errorMessage, toastId);
        },
        onAccessDenied: ()=>{
            showErrorToast('Failed to connect your email account. Please try again.', 'integrations-access-denied');
        }
    });
    const handleConnectToQbo = async ()=>{
        setIsLoading(true);
        const linkUrl = await connectToQbo();
        if (linkUrl) {
            const connectUrl = new URL(linkUrl);
            connectUrl.searchParams.append('host', window.location.host);
            changeLocation(connectUrl.toString());
        } else {
            await fetchIntegrations();
            setIsLoading(false);
        }
    };
    return /*#__PURE__*/ _jsxs(Switch, {
        children: [
            /*#__PURE__*/ _jsx(Route, {
                path: routes.qboSetup,
                render: ()=>/*#__PURE__*/ _jsx(AccountingSetupPage, {
                        accounting: Accounting.QBO
                    })
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.xeroSetup,
                render: ()=>/*#__PURE__*/ _jsx(AccountingSetupPage, {
                        accounting: Accounting.Xero
                    })
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.slackIntegration,
                component: SlackViaZapierPage
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.hubspotIntegration,
                component: HubspotViaZapierPage
            }),
            financialCentsIntegration && integrationModel.isLinked(financialCentsIntegration) && /*#__PURE__*/ _jsx(Route, {
                path: routes.financialCentsSetup,
                render: ()=>/*#__PURE__*/ _jsx(FinancialCentsSetupPage, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.karbonSetup,
                render: ()=>/*#__PURE__*/ _jsx(KarbonSetupPage, {
                        integration: karbonIntegration
                    })
            }),
            clientHubIntegration && /*#__PURE__*/ _jsx(Route, {
                path: routes.clientHubSetup,
                render: ()=>/*#__PURE__*/ _jsx(ClientHubSetupPage, {
                        integration: clientHubIntegration
                    })
            }),
            keeperIntegration && integrationModel.isLinked(keeperIntegration) && /*#__PURE__*/ _jsx(Route, {
                path: routes.keeperSetup,
                render: ()=>/*#__PURE__*/ _jsx(KeeperSetupPage, {
                        integration: keeperIntegration
                    })
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: routes.availableApps,
                children: error ? /*#__PURE__*/ _jsx(PageErrorState, {
                    testId: "integrations-error-state",
                    title: title,
                    onRefresh: ()=>fetchIntegrations()
                }) : /*#__PURE__*/ _jsxs(Page, {
                    testId: "integrations-page",
                    header: title,
                    subheader: subheader,
                    children: [
                        isLoading && /*#__PURE__*/ _jsx(FunctionalLoader, {
                            show: isLoading,
                            name: "loadingIntegrations"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.section,
                            children: [
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: "Accounting software",
                                    children: [
                                        !!integrations && /*#__PURE__*/ _jsx(QboCard, {
                                            connect: handleConnectToQbo
                                        }),
                                        xeroIntegration && /*#__PURE__*/ _jsx(XeroCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: "Communications",
                                    children: [
                                        gmailIntegration && /*#__PURE__*/ _jsx(GmailCard, {
                                            integration: gmailIntegration,
                                            isDisabled: !!outlookIntegration && integrationModel.isLinked(outlookIntegration)
                                        }),
                                        outlookIntegration && /*#__PURE__*/ _jsx(OutlookCard, {
                                            integration: outlookIntegration,
                                            isDisabled: !!gmailIntegration && integrationModel.isLinked(gmailIntegration)
                                        }),
                                        /*#__PURE__*/ _jsx(SlackViaZapierCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: "Workflow automation",
                                    children: [
                                        /*#__PURE__*/ _jsx(MakeCard, {}),
                                        /*#__PURE__*/ _jsx(ZapierCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: "Practice management",
                                    children: [
                                        clientHubIntegration && /*#__PURE__*/ _jsx(ClientHubCard, {
                                            integration: clientHubIntegration
                                        }),
                                        /*#__PURE__*/ _jsx(CountCard, {}),
                                        financialCentsIntegration && /*#__PURE__*/ _jsx(FinancialCentsCard, {
                                            integration: financialCentsIntegration
                                        }),
                                        karbonIntegration && /*#__PURE__*/ _jsx(KarbonCard, {
                                            integration: karbonIntegration
                                        }),
                                        keeperIntegration && /*#__PURE__*/ _jsx(KeeperCard, {
                                            integration: keeperIntegration
                                        }),
                                        /*#__PURE__*/ _jsx(LevvyCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: "CRM",
                                    children: [
                                        /*#__PURE__*/ _jsx(HubspotViaZapierCard, {}),
                                        /*#__PURE__*/ _jsx(MondayCard, {})
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
